<template>
  <a-modal v-model:open="batchImportModal.show" :title="batchImportModal.title" centered :width="batchImportModal.state === 1 ? '500px' : '80%'">
    <template v-if="batchImportModal.state === 1">
      <a-button type="primary" class="select-btn" @click="clickSelect">
        选择文件
        <input
          type="file"
          @change="selectedFile"
          ref="selectFileRef"
          accept=".csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        />
      </a-button>
      <div v-if="batchImportModal.file" style="font-size: 12px">{{ batchImportModal.file.name }}</div>
    </template>
    <template v-else>
      <div class="common-select">
        <div class="select-item">
          <span>供应商：</span>
          <a-select
            style="width: 180px"
            v-model:value="batchImportModal.commonSelect.sup_id"
            :options="supplierList"
            placeholder="请选择供应商"
            show-search
            optionFilterProp="label"
            :disabled="hasAuthority(['29'])"
          >
            <template #dropdownRender="{ menuNode: menu }">
              <v-nodes :vnodes="menu" />
              <a-divider style="margin: 4px 0" />
              <div class="select-add-item" @mousedown="(e) => e.preventDefault()" @click="addSupplier">
                <ehf-icon icon="add-circle-line"></ehf-icon>
                <span style="margin-left: 10px">新增供应商</span>
              </div>
            </template>
          </a-select>
        </div>
        <div class="select-item">
          <span>单位：</span>
          <a-select style="width: 180px" v-model:value="batchImportModal.commonSelect.unit" option-label-prop="label" placeholder="请选择单位" show-search>
            <a-select-option class="tag-item" v-for="item in unitList" :value="item.label" :key="item.value" :label="item.label">
              {{ item.label }}
              <ehf-icon icon="close-circle-fill" class="close-icon" style="float: right" @click="deleteUnit($event, item.value)" />
            </a-select-option>
            <template #dropdownRender="{ menuNode: menu }">
              <v-nodes :vnodes="menu" />
              <a-divider style="margin: 4px 0" />
              <div class="select-add-item" @mousedown="(e) => e.preventDefault()" @click="addUnit">
                <ehf-icon icon="add-circle-line"></ehf-icon>
                <span style="margin-left: 10px">新增单位</span>
              </div>
            </template>
          </a-select>
        </div>
      </div>
      <a-table
        bordered
        class="sku-table-css"
        size="small"
        :data-source="batchImportModal.list"
        :pagination="batchImportModal.list.length <= 10 ? false : { pageSize: 10 }"
        rowKey="id"
        :customRow="customRow"
      >
        <a-table-column key="sort" data-index="sort" title="序号">
          <template #default="{ record }">
            <el-input :modelValue="record.sort" placeholder="字母+数字" :maxlength="3" @input="(val) => sortChange(val, record)" />
          </template>
        </a-table-column>
        <a-table-column key="name" data-index="name" title="商品名称">
          <template #default="{ record }">
            <el-input type="textarea" v-model="record.name" placeholder="请输入商品名称" :maxlength="50" :autosize="{ minRows: 1 }" />
          </template>
        </a-table-column>
        <a-table-column key="remark_name" data-index="remark_name" title="备注名称">
          <template #default="{ record }">
            <el-input type="textarea" v-model="record.remark_name" placeholder="请输入备注名称" :maxlength="50" :autosize="{ minRows: 1 }" />
          </template>
        </a-table-column>
        <a-table-column key="weight" data-index="weight" title="重量">
          <template #default="{ record }">
            <el-input :modelValue="record.weight" suffix="kg" placeholder="如:0.2" @input="(value) => weightchange({ target: { value } }, record)" />
          </template>
        </a-table-column>
        <a-table-column key="pack_size" data-index="pack_size" title="装箱规格">
          <template #default="{ record }">
            <el-input v-model="record.pack_size" placeholder="请输入装箱规格" :maxlength="20" />
          </template>
        </a-table-column>
        <a-table-column key="product_code" data-index="product_code" title="货号">
          <template #default="{ record }">
            <el-input v-model="record.product_code" placeholder="请输入货号" :maxlength="20" />
          </template>
        </a-table-column>
      </a-table>
    </template>
    <template #footer>
      <template v-if="batchImportModal.state === 1">
        <a style="margin-right: 20px" href="/static/skuTemplate/children.xlsx" download="批量导入子商品模板.xlsx">下载模板</a>
      </template>
      <a-button type="primary" :loading="batchImportModal.importing" @click="confirmImport">确认导入</a-button>
    </template>
  </a-modal>
</template>

<script setup>
  import { getExcelContent } from '@/api/common'
  import { message } from 'ant-design-vue'
  import { reactive, ref, toRefs, watch } from 'vue'
  import { hasAuthority } from '@/utils'
  import { nanoid } from 'nanoid'

  const props = defineProps({
    getContainer: {
      type: Function,
      default: () => {
        return document.body
      },
    },
    supplierList: {
      type: Array,
      default: () => {
        return []
      },
    },
    unitList: {
      type: Array,
      default: () => {
        return []
      },
    },
  })
  const { getContainer, supplierList, unitList } = toRefs(props)
  const emit = defineEmits(['save', 'addSupplier', 'addUnit', 'deleteUnit'])
  message.config({
    getContainer: getContainer.value,
  })
  watch(getContainer, (val) => {
    message.config({
      getContainer: val,
    })
  })

  const VNodes = (_, { attrs }) => {
    return attrs.vnodes
  }
  const batchImportModal = reactive({
    show: false,
    state: 1, // 1-待上传，2-待导入
    file: null,
    title: '批量导入',
    importing: false,
    list: [],
    commonSelect: {
      unit: null,
      sup_id: null,
    },
  })
  const importMapping = {
    sort: '序号',
    name: '商品名称',
    remark_name: '备注名称',
    weight: '重量',
    pack_size: '装箱规格',
    product_code: '货号',
  }
  watch(supplierList, (val) => {
    //判断是否是供应商身份，供应商身份默认选择自己并禁用
    if (hasAuthority(['29']) && val.length > 0) {
      batchImportModal.commonSelect.sup_id = supplierList.value[0].value
    }
  })
  // 批量导入
  const batchImport = () => {
    batchImportModal.title = '批量新增-子商品'
    batchImportModal.state = 1
    batchImportModal.file = null
    batchImportModal.show = true
  }
  // 点击选择文件按钮
  const selectFileRef = ref(null)
  const clickSelect = () => {
    selectFileRef.value.click()
  }
  // 选择文件
  const selectedFile = (e) => {
    batchImportModal.file = e.target.files[0]
  }
  // 确认导入
  const confirmImport = async () => {
    if (batchImportModal.state === 1) {
      // 上传文件
      if (!batchImportModal.file) {
        return message.error('请选择文件')
      }
      selectFileRef.value.value = ''
      const body = {
        file: batchImportModal.file,
        mapping: JSON.stringify(importMapping),
      }
      batchImportModal.importing = true
      const res = await getExcelContent(body)
      batchImportModal.importing = false
      if (res.code === 0) {
        handleImportInfo(res.data.list)
        batchImportModal.state = 2
      } else {
        message.error(res.msg)
      }
    } else {
      // 插入数据
      emit('save', batchImportModal.list, batchImportModal.commonSelect.sup_id, batchImportModal.commonSelect.unit)
    }
  }
  const handleImportInfo = (data) => {
    data = data.map((v) => {
      v.id = nanoid()
      v.is_new = true
      v.sup_id = null
      v.img_url = ''
      v.unit = null
      v.stocks = 0
      v.presale_stocks = 0
      return v
    })
    batchImportModal.list = data
  }
  const addSupplier = () => {
    emit('addSupplier')
  }
  const addUnit = () => {
    emit('addUnit')
  }
  const deleteUnit = (e, id) => {
    emit('deleteUnit', e, id)
  }

  // -----行点击高亮-----
  const highlightId = ref('')
  const customRow = (record) => {
    return {
      class: {
        highlight: record.id == highlightId.value,
      },
      onClick: () => {
        highlightId.value = record.id
      },
    }
  }

  defineExpose({
    batchImportModal,
    batchImport,
  })
</script>

<style lang="scss" scoped>
  .select-add-item {
    padding: 4px 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
  }

  .select-btn {
    input {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      z-index: 1;
      width: 100%;
      height: 100%;
      opacity: 0;
      display: none;
    }
  }
  .common-select {
    display: flex;
    align-items: center;
    margin-bottom: 10px;
    .select-item {
      margin-right: 10px;
    }
  }
</style>
